import style from './style.module.scss'
import classnames from 'classnames'
import { Dispatch, SetStateAction, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateAffiliateAttribute } from 'store/slices/affiliate'
import { Button } from '@crossfit/design-system'
import { ReactComponent as DeleteIcon } from 'components/Admin/images/delete.svg'
import { ReactComponent as ReorderIcon } from 'components/Admin/images/reorder.svg'
import { deleteAffiliatePhoto } from 'services/affiliates/imageService/delete'
import { Loader } from './Loader'
import { useFormNotification } from 'hooks/useFormNotification'
import { getAffiliate } from 'store/selectors'
import {
  updateGalleryPhotos,
  updatePrimaryPhoto,
} from 'store/slices/DirectoryPage/photos'

type Props = {
  type: 'primary' | 'gallery' | 'schedule'
  imageUrl?: string
  setPrimaryImage?: Dispatch<SetStateAction<string>>
  setGalleryImages?: Dispatch<
    SetStateAction<{ url?: string; order?: number }[]>
  >
  setScheduleImage?: (newUrl: string) => void
  isReordering?: boolean
}

const ImageView = ({
  type,
  imageUrl,
  setPrimaryImage,
  setGalleryImages,
  setScheduleImage,
  isReordering,
}: Props) => {
  const dispatch = useDispatch()
  const affiliateDetail = useSelector(getAffiliate)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false)
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false)
  const handleFormNotification = useFormNotification()
  const defaultImageUrl =
    'wp-content/uploads/affiliates/default-profile-photo.png'
  const isDefault = imageUrl?.includes(defaultImageUrl)

  const onDeleteClick = async () => {
    setIsImageLoading(true)

    const newAffiliateImages = await deleteAffiliatePhoto(
      affiliateDetail.id,
      imageUrl.substring(imageUrl?.lastIndexOf('/') + 1),
      type,
    )

    setIsImageLoading(false)

    if (newAffiliateImages) {
      if (type === 'primary') {
        dispatch(updatePrimaryPhoto(newAffiliateImages.primary.url))
        setPrimaryImage(newAffiliateImages.primary.url)
      } else if (type === 'gallery') {
        const sortedGallery =
          newAffiliateImages.gallery && newAffiliateImages.gallery
            ? newAffiliateImages.gallery
                .sort((a, b) => a.order - b.order)
                .map((image, i) => ({
                  url: image?.url,
                  order: image?.order,
                }))
            : []

        dispatch(updateGalleryPhotos(sortedGallery))
      } else if (type === 'schedule') {
        setScheduleImage(newAffiliateImages.schedule?.url)
      }

      setIsConfirmingDelete(false)
      handleFormNotification('success')
    }
  }

  if (isImageLoading) {
    return <Loader />
  } else {
    return (
      <div className={style.imageContainer}>
        <img
          src={imageUrl}
          className={classnames(style.image, isDefault && style.imageDefault)}
        />
        {!isDefault && !isConfirmingDelete && !isReordering ? (
          <button
            className={style.imageButtonContainer}
            aria-label='delete-photo'
            onClick={() => setIsConfirmingDelete(true)}
          >
            <DeleteIcon />
          </button>
        ) : null}
        {isReordering ? (
          <button
            className={style.imageButtonContainer}
            aria-label='reorder-photo'
            onClick={() => setIsConfirmingDelete(true)}
          >
            <ReorderIcon />
          </button>
        ) : null}
        {!isDefault && isConfirmingDelete ? (
          <div className={style.imageButtonContainer}>
            <Button
              onClick={() => setIsConfirmingDelete(false)}
              size='sm'
              variant='secondary'
              color='white'
            >
              Cancel
            </Button>
            <Button
              onClick={() => onDeleteClick()}
              size='sm'
              variant='destructive'
            >
              Delete
            </Button>
          </div>
        ) : null}
      </div>
    )
  }
}

export default ImageView
